import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getTechnicianComments, postTechnicianComment } from '../api/dms/technicianComments.api';
import { logConsole, logTransaction } from '../common/log';
import { getStateDateAsString, getStateStringAsDate, handleThunkError } from '../common/util';
import { ITechCommentRequestQueueItem, RequestQueueGroup, RequestQueueType } from '../models/dms/requestQueue.model';
import { UNKNOWN_TECH } from '../models/dms/technician.model';
import { ITechComment, ITechCommentPost } from '../models/dms/technicianComments.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { addToRequestQueueStoreAsync } from './requestQueueSlice';

interface ITechnicianCommentState {
  selectedTechnicianComments: ITechComment[];
  selectedTechnicianCommentsLoading: boolean;
  adhocCommentsCache: ITechComment[];
}

const initialState: ITechnicianCommentState = {
  selectedTechnicianComments: [],
  selectedTechnicianCommentsLoading: false,
  adhocCommentsCache: [],
};

/// THUNKS

export const setSelectedTechnicianCommentsStoreAsync = createAsyncThunk<{}, ITechComment[]>('technicianComment/setSelectedTechnicianCommentsStoreAsync', async (techComments, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected technician comments to storage`, '[technicianComment] setSelectedTechnicianCommentsStoreAsync');
  try {
    dispatch(setSelectedTechnicianCommentsState(techComments));
    await updateConfigObject<ITechComment[]>(ConfigName.SelectedTechComments, techComments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchTechnicianCommentsStoreAsync = createAsyncThunk<{}, number>('technicianComment/fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync', async (repairOrderNumber, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching technician comments from database by RO '${repairOrderNumber}'`, '[technicianComment] fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setSelectedTechnicianCommentsLoadingState(true));

    const responseData = await getTechnicianComments(repairOrderNumber);

    await dispatch(setSelectedTechnicianCommentsStoreAsync(responseData));

    dispatch(setIsLoadingCountState(-1));
    dispatch(setSelectedTechnicianCommentsLoadingState(false));

    return responseData;
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSelectedTechnicianCommentsLoadingState(false));
  }
});

export const addTechnicianCommentAsync = createAsyncThunk<{}, { repairOrderNumber: number; branchCode: string; jobType: string; jobCode: string; comment: string; isAlert: boolean; isAdhoc: boolean }>(
  'technicianComment/addTechnicianCommentAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Adding tech comment '${(payload.comment || '').substring(0, 10)}...' - repairOrderNumber: ${payload.repairOrderNumber}, jobType: ${payload.jobCode}`, '[technicianClocking] addTechnicianCommentAsync');
    try {
      const state = getState() as RootState;
      const { technician } = state.auth;

      if (payload.isAdhoc) {
        logTransaction(LogType.Info, `Tech comment is for an adhoc clocking`);
        // For Adhoc clocking, instead of posting the comments to the endpoint, keep them in cache and post them together with the clocking
        const techComment = {
          repairOrderNumber: payload.repairOrderNumber,
          branch: payload.branchCode,
          jobType: payload.jobType,
          jobCode: payload.jobCode,
          technicianCode: technician?.code || UNKNOWN_TECH,
          comment: payload.comment,
          isAlert: payload.isAlert,
          actioned: false,
          createdDateTime: getStateDateAsString(new Date()),
          technicianDisplayName: technician?.displayName || '',
        } as ITechComment;
        await dispatch(addAdhocCommentCacheStoreAsync(techComment));
        await dispatch(addQueueToSelectedTechnicianCommentsStoreAsync({ data: techComment, id: new Date().getTime() }));
      } else {
        logTransaction(LogType.Info, `Tech comment is for a normal clocking`);
        const techComment = {
          id: new Date().getTime(),
          group: RequestQueueGroup.TechComment,
          type: RequestQueueType.TechComment,
          immediate: true,
          data: {
            repairOrderNumber: payload.repairOrderNumber,
            branch: payload.branchCode,
            jobType: payload.jobType,
            jobCode: payload.jobCode,
            technicianCode: technician?.code || UNKNOWN_TECH,
            comment: payload.comment,
            isAlert: payload.isAlert,
            actioned: false,
            createdDateTime: getStateDateAsString(new Date()),
            technicianDisplayName: technician?.displayName || '',
            unSync: true,
          } as ITechComment,
        } as ITechCommentRequestQueueItem;
        await dispatch(addToRequestQueueStoreAsync(techComment));
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const addQueueToSelectedTechnicianCommentsStoreAsync = createAsyncThunk<{}, { data: ITechComment; id: number }>('technicianComment/addQueueToSelectedTechnicianCommentsStoreAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding tech comment queued item to selected tech comments`, '[technicianComment] addQueueToSelectedTechnicianCommentsStoreAsync');
  try {
    let currentComments = [...(getState() as RootState).technicianComments.selectedTechnicianComments];
    let newComment = {
      ...payload.data,
      unSync: true,
      id: payload.id,
    } as ITechComment;

    currentComments = [...currentComments, newComment];

    const sortedData = currentComments.sort((x, y) => +getStateStringAsDate(y.createdDateTime) - +getStateStringAsDate(x.createdDateTime));
    await dispatch(setSelectedTechnicianCommentsStoreAsync(sortedData));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const updateQueueToSelectedTechnicianCommentStoreAsync = createAsyncThunk<{}, number>('technicianComment/updateQueueToSelectedTechnicianCommentStoreAsync', async (id, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Updating technician comment, setting index ${id} to unSync: false`, '[technicianComment] updateQueueToSelectedTechnicianCommentStoreAsync');
  try {
    let currentComments = [...(getState() as RootState).technicianComments.selectedTechnicianComments];
    const commentItemIndex = currentComments.findIndex((hist) => hist.id === id);
    if (commentItemIndex >= 0) {
      const commentItem = {
        ...currentComments[commentItemIndex],
        unSync: false,
      };
      currentComments.splice(commentItemIndex, 1, commentItem);
    }

    const sortedData = currentComments.sort((x, y) => +getStateStringAsDate(y.createdDateTime) - +getStateStringAsDate(x.createdDateTime));
    await dispatch(setSelectedTechnicianCommentsStoreAsync(sortedData));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const removeSelectedTechnicianCommentStoreAsync = createAsyncThunk<{}, number>('technicianComment/removeSelectedTechnicianCommentStoreAsync', async (id, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Removing comment id ${id}`, '[technicianComment] removeSelectedTechnicianCommentStoreAsync');
  try {
    let currentComments = [...(getState() as RootState).technicianComments.selectedTechnicianComments];
    if (currentComments.length) {
      const comments = currentComments.filter((c) => c.id !== id);
      await dispatch(setSelectedTechnicianCommentsStoreAsync(comments));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const postTechnicianCommentAsync = createAsyncThunk<{}, ITechComment>('technicianComment/postTechnicianCommentAsync', async (comment, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `POST technician comment '${(comment.comment || '').substring(0, 10)}...' to database - repairOrderNumber: ${comment.repairOrderNumber}, jobType: ${comment.jobCode}`, '[technicianComment] postTechnicianCommentAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    await postTechnicianComment({
      repairOrderNumber: comment.repairOrderNumber,
      branch: comment.branch,
      jobType: comment.jobType,
      jobCode: comment.jobCode,
      technicianCode: comment?.technicianCode || UNKNOWN_TECH,
      comment: comment.comment,
      isAlert: comment.isAlert,
      actioned: false,
    } as ITechCommentPost);
    dispatch(setIsLoadingCountState(-1));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const setAdhocCommentCacheStoreAsync = createAsyncThunk<{}, ITechComment[]>('technicianComment/setAdhocCommentCacheStoreAsync', async (comments, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting adhoc comment cache to storage`, '[technicianComment] setAdhocCommentCacheStoreAsync');
  try {
    dispatch(setAdhocCommentsCacheState(comments));
    await updateConfigObject<ITechComment[]>(ConfigName.AdhocTechCommentsCache, comments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const addAdhocCommentCacheStoreAsync = createAsyncThunk<{}, ITechComment>('technicianComment/addAdhocCommentCacheStoreAsync', async (comment, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding adhoc comment cache to storage`, '[technicianComment] addAdhocCommentCacheStoreAsync');
  try {
    const newComments = [...(getState() as RootState).technicianComments.adhocCommentsCache, comment];
    dispatch(setAdhocCommentsCacheState(newComments));
    await updateConfigObject<ITechComment[]>(ConfigName.AdhocTechCommentsCache, newComments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

/// SLICE

export const technicianCommentSlice = createSlice({
  name: 'technicianComment',
  initialState,
  reducers: {
    setSelectedTechnicianCommentsState: (state, action: PayloadAction<ITechComment[]>) => {
      logConsole(LogType.Info, '[technicianComment] setSelectedTechnicianCommentsState');
      state.selectedTechnicianComments = action.payload;
    },
    setSelectedTechnicianCommentsLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[technicianComment] setSelectedTechnicianCommentsLoadingState: ${action.payload}`);
      state.selectedTechnicianCommentsLoading = action.payload;
    },
    setAdhocCommentsCacheState: (state, action: PayloadAction<ITechComment[]>) => {
      logConsole(LogType.Info, `[technicianComment] setAdhocCommentsCacheState`);
      state.adhocCommentsCache = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedTechnicianCommentsState, setSelectedTechnicianCommentsLoadingState, setAdhocCommentsCacheState } = technicianCommentSlice.actions;

/// CUSTOM SELECTORS

export default technicianCommentSlice.reducer;
